'use client';

import { useMemo } from 'react';
import { Select, SelectItem } from './Select';

interface TimePickerProps {
  /** "HH:MM" 24-hour string. Empty allowed only when `allowEmpty` is true. */
  value: string;
  onChange: (next: string) => void;
  disabled?: boolean;
  /** Granularity for minute options. Defaults to 1 (every minute). */
  minuteStep?: number;
  /** When true, no leading zero is enforced on display (still on emit). */
  className?: string;
}

const HOURS = Array.from({ length: 24 }, (_, i) => pad2(i));

// Two stacked dropdowns (HH, MM) that look identical to the rest of the
// form. Both options lists are Plex Mono with tabular-nums so any time
// renders in a stable column. Use exactly like an <input type="time">.
export function TimePicker({
  value,
  onChange,
  disabled,
  minuteStep = 1,
  className,
}: TimePickerProps) {
  const minutes = useMemo(() => {
    const step = Math.max(1, Math.min(60, minuteStep));
    return Array.from({ length: Math.floor(60 / step) }, (_, i) => pad2(i * step));
  }, [minuteStep]);

  const [hh, mm] = splitTime(value);
  // Snap an arbitrary minute (e.g. saved as 23) to the closest option so the
  // Select shows a valid option as selected even when granularity narrowed.
  const mmSnapped = useMemo(() => snapToOptions(mm, minutes), [mm, minutes]);

  function setHour(next: string) {
    onChange(`${next}:${mmSnapped}`);
  }
  function setMinute(next: string) {
    onChange(`${hh}:${next}`);
  }

  return (
    <div className={className ?? 'inline-flex items-center gap-2'}>
      <Select
        value={hh}
        onValueChange={setHour}
        ariaLabel="Hour"
        mono
        disabled={disabled}
        triggerClassName="w-[4.5rem]"
      >
        {HOURS.map((h) => (
          <SelectItem key={h} value={h} mono>
            {h}
          </SelectItem>
        ))}
      </Select>
      <span className="font-mono text-sm text-subtle-foreground">:</span>
      <Select
        value={mmSnapped}
        onValueChange={setMinute}
        ariaLabel="Minute"
        mono
        disabled={disabled}
        triggerClassName="w-[4.5rem]"
      >
        {minutes.map((m) => (
          <SelectItem key={m} value={m} mono>
            {m}
          </SelectItem>
        ))}
      </Select>
    </div>
  );
}

function pad2(n: number): string {
  return String(n).padStart(2, '0');
}

function splitTime(value: string): [string, string] {
  if (!value || !value.includes(':')) return ['09', '00'];
  const [h, m] = value.split(':');
  return [pad2(Number(h ?? 0)), pad2(Number(m ?? 0))];
}

function snapToOptions(value: string, options: string[]): string {
  if (options.includes(value)) return value;
  const target = Number(value);
  let best = options[0]!;
  let bestDelta = Math.abs(Number(best) - target);
  for (const opt of options) {
    const d = Math.abs(Number(opt) - target);
    if (d < bestDelta) {
      best = opt;
      bestDelta = d;
    }
  }
  return best;
}
